/**
 * MessagingSuccessPage · iter138
 * Bestätigung nach Kauf eines Nachrichten-Pakets.
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, MessageCircle, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";

export default function MessagingSuccessPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const purchaseId = params.get("purchase_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!purchaseId) { setStatus("unknown"); return; }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get("/me/message-credits");
        const cr = (data?.credits || []).find((c) => c.credit_id === purchaseId);
        if (cr?.status === "PAID") { if (!cancelled) setStatus("paid"); return; }
      } catch (e) { /* ignore */ }
      tries += 1;
      if (tries < 6 && !cancelled) setTimeout(poll, 2000);
      else if (!cancelled) setStatus("pending");
    };
    poll();
    return () => { cancelled = true; };
  }, [purchaseId]);

  return (
    <div data-testid="messaging-success-page" className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-50 border border-emerald-200 mb-6">
          {status === "paid" ? (
            <CheckCircle2 size={36} className="text-emerald-600" data-testid="messaging-success-paid" />
          ) : (
            <MessageCircle size={36} className="text-emerald-600 animate-pulse" />
          )}
        </div>
        <h1 className="text-2xl font-bold text-[#0A0A0A] mb-2">
          {status === "paid"
            ? t("msg_success.title_paid", { defaultValue: "Nachrichten-Paket freigeschaltet!" })
            : t("msg_success.title_pending", { defaultValue: "Zahlung wird verarbeitet …" })}
        </h1>
        <p className="text-sm text-[#666] mb-8">
          {status === "paid"
            ? t("msg_success.body_paid", { defaultValue: "Dein Nachrichten-Guthaben steht sofort bereit — starte jetzt eine Konversation." })
            : t("msg_success.body_pending", { defaultValue: "Wir warten auf Bestätigung von Stripe. Das dauert normalerweise wenige Sekunden." })}
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <button
            data-testid="msg-success-back"
            onClick={() => navigate(-1)}
            className="h-11 px-5 rounded-full bg-[#F5F5F5] text-[#0A0A0A] text-sm font-medium hover:bg-[#EAEAEA] flex items-center justify-center gap-2">
            <ArrowLeft size={16} /> {t("msg_success.back", { defaultValue: "Zurück" })}
          </button>
          <button
            data-testid="msg-success-messages"
            onClick={() => navigate("/messages")}
            className="h-11 px-5 rounded-full bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#222]">
            {t("msg_success.to_messages", { defaultValue: "Zu meinen Nachrichten" })}
          </button>
        </div>
      </div>
    </div>
  );
}
